/**
 * Smartlead lead-category name constants.
 *
 * Comparison convention: Smartlead category names arrive as free text and the
 * dashboard stores them verbatim (the DB intentionally keeps casing variants
 * such as 'not_interested' vs 'Not Interested'). NEVER normalize names on
 * write — always compare with `name.toLowerCase().trim()` against the
 * lowercased constants below.
 *
 * DTC categories (Aug 2026): DTC / F&B-wholesale accounts convert via samples
 * and wholesale orders rather than meetings. Positive-sentiment categories
 * created in Smartlead that must count wherever positives count:
 *   - 'DTC - Missing POC Info'        (category id 170785, created 2026-08-18)
 *   - 'DTC - Wholesale Order Created' (category id 172501, created 2026-08-21)
 *   - 'Sample Request'                (category id 4507,   first seen 2026-08-20)
 * They are NOT Appointment Booked equivalents: never set appointment_booked_at
 * or has_converted for them, and they stay out of meeting-based incentive
 * counts (qualified_appointments_monthly). The DTC dashboard instead counts
 * conversions from the current category ('Sample Request' and
 * 'DTC - Wholesale Order Created') plus the legacy appointment_booked_at.
 *
 * STRN MQL (Aug 2026): client STRN (Smartlead client_id 245055) measures goal
 * attainment by 'MQL' — a category tagged from their own Smartlead account.
 * Ingestion-wise it behaves like a DTC positive (creates interested_leads rows
 * via the interested family); incentive-wise it gets its own calculation_type
 * 'mql' (counts MQL-tagged leads by last_interested_at in period), so the two
 * must not be conflated.
 */

/** Exact Smartlead name of the wholesale-order conversion category. */
export const DTC_WHOLESALE_ORDER_CATEGORY_NAME = 'DTC - Wholesale Order Created';

/** Exact Smartlead names of the DTC positive categories (display casing). */
export const DTC_CATEGORY_NAMES: readonly string[] = [
  'DTC - Missing POC Info',
  DTC_WHOLESALE_ORDER_CATEGORY_NAME,
];

/**
 * Exact Smartlead name of the sample-request category (display casing).
 * Tagged on DTC accounts when a lead asks for product samples — the DTC
 * funnel's conversion signal (the legacy equivalent was the 'Appointment
 * Booked' tag). Ingestion-wise it is part of the Interested family.
 *
 * 2026-10-16: Smartlead renamed this category in place — id 4507 is now
 * 'DTC: Sample Request' (was 'Sample Request'). Both spellings must match
 * everywhere until the id-based migration retires name matching.
 */
export const SAMPLE_REQUEST_CATEGORY_NAME = 'Sample Request';

/** All spellings of the sample-request category (pre- and post-2026-10-16 rename). */
export const SAMPLE_REQUEST_CATEGORY_NAMES: readonly string[] = [
  SAMPLE_REQUEST_CATEGORY_NAME,
  'DTC: Sample Request',
];

/** Exact Smartlead name of STRN's MQL category (display casing). */
export const MQL_CATEGORY_NAME = 'MQL';

/**
 * Exact Smartlead name of the forwarded marker (display casing). Tagged
 * manually by the team when a lead's email has already been forwarded to the
 * client — "do not forward again". It is a full member of the Interested /
 * positive family: the lead keeps counting (DTC Positives, Interested filter)
 * after the re-tag, and a Forwarded-first tag creates an interested_leads row.
 * Deliberately NOT client-portal-visible (internal working marker, like
 * Pending) and NOT a DTC conversion.
 */
export const FORWARDED_CATEGORY_NAME = 'Forwarded';

/**
 * All spellings of the forwarded marker (pre- and post-2026-10-16 rename:
 * Smartlead renamed id 20602 'Forwarded' → 'DTC: Handed Over' in place; and the
 * approved 2026-10-27 protocol rename 'Handed Over' → 'Handed Over/Sample
 * Requested', same id).
 */
export const FORWARDED_CATEGORY_NAMES: readonly string[] = [
  FORWARDED_CATEGORY_NAME,
  'DTC: Handed Over',
  'Handed Over',
  'Handed Over/Sample Requested',
];

/**
 * Exact Smartlead name of the pending-info hold category (display casing).
 * Tagged when the IM replies asking the prospect for more information before
 * they can respond. Display-wise the lead stays in the Interested view (the
 * follow-up is in flight), but it is NOT a positive outcome: it stays out of
 * the ingestion family (INTERESTED_FAMILY_CATEGORIES) and the positive family,
 * so analytics/incentives/row-creation semantics are unchanged.
 */
export const PENDING_INFO_CATEGORY_NAME = 'Pending: Need Additional Info before Response';

/**
 * Categories that count as a DTC conversion (a sample request or a wholesale
 * order) on the DTC dashboard. 'Appointment Booked' is not listed here — it is
 * counted separately via appointment_booked_at, which it is the only category
 * allowed to set.
 *
 * 2026-10-27 protocol: Handed Over / Sample Requested (id 20602) IS the
 * sample-request outcome (DTC ABs migrate 240 → 20602 and complete sample
 * requests land there directly), so all its spellings count as conversions.
 */
export const DTC_CONVERSION_CATEGORY_NAMES: readonly string[] = [
  ...SAMPLE_REQUEST_CATEGORY_NAMES,
  DTC_WHOLESALE_ORDER_CATEGORY_NAME,
  ...FORWARDED_CATEGORY_NAMES,
];

/** DTC_CONVERSION_CATEGORY_NAMES lowercased (for SQL `lower(...) = ANY(...)`). */
export const DTC_CONVERSION_CATEGORY_NAMES_LOWER: readonly string[] =
  DTC_CONVERSION_CATEGORY_NAMES.map((name) => name.toLowerCase());

/** Lowercased DTC names for set-membership checks. */
export const DTC_CATEGORY_NAMES_LOWER: readonly string[] =
  DTC_CATEGORY_NAMES.map((name) => name.toLowerCase());

/**
 * Ingestion "Interested family": categories that must create an
 * interested_leads row when a lead's first positive tag lands directly on them
 * (webhook-processor `shouldCreateIfMissing`). 'appointment booked' is
 * intentionally excluded — the processor handles it separately because it also
 * sets appointment_booked_at / has_converted.
 */
export const INTERESTED_FAMILY_CATEGORIES: ReadonlySet<string> = new Set([
  'interested',
  'automated followups begun',
  'interested: automated fu begun',
  'interested: manual fu begun',
  ...DTC_CATEGORY_NAMES_LOWER,
  ...SAMPLE_REQUEST_CATEGORY_NAMES.map((name) => name.toLowerCase()),
  MQL_CATEGORY_NAME.toLowerCase(),
  ...FORWARDED_CATEGORY_NAMES.map((name) => name.toLowerCase()),
]);

/**
 * Full positive family: Interested variants + DTC positives + Appointment
 * Booked. Anything in this set counts as a "positive" outcome (IM analytics
 * membership, DTC dashboard positives, positive-response counting).
 */
export const POSITIVE_CATEGORY_FAMILY: ReadonlySet<string> = new Set([
  ...INTERESTED_FAMILY_CATEGORIES,
  'appointment booked',
]);

/**
 * Display expansion for a user-facing 'Interested' category filter: every
 * current_category_name a lead can hold and still belong to the Interested
 * view. Used by filter-builder / query-builder / unified-leads so KPI counts,
 * table rows and exports stay identical (the four filter implementations must
 * not drift).
 */
export const INTERESTED_CATEGORY_EXPANSION: readonly string[] = [
  'Interested',
  'Automated Followups Begun',
  'Interested: Automated FU Begun',
  'Interested: Manual FU Begun',
  'Appointment Booked',
  ...DTC_CATEGORY_NAMES,
  ...SAMPLE_REQUEST_CATEGORY_NAMES,
  MQL_CATEGORY_NAME,
  PENDING_INFO_CATEGORY_NAME,
  ...FORWARDED_CATEGORY_NAMES,
];

/** INTERESTED_CATEGORY_EXPANSION lowercased (for SQL `lower(...) IN (...)` lists). */
export const INTERESTED_CATEGORY_EXPANSION_LOWER: readonly string[] =
  INTERESTED_CATEGORY_EXPANSION.map((name) => name.toLowerCase());

/**
 * Every Smartlead category name known to the pipeline (lowercased, including
 * legacy casing variants seen in production). Used ONLY by the unknown-category
 * watch guard: a webhook arriving with a name outside this set is logged —
 * never dropped — so a newly created Smartlead category surfaces immediately
 * instead of silently changing dashboard behaviour.
 */
export const KNOWN_SMARTLEAD_CATEGORY_NAMES: ReadonlySet<string> = new Set([
  ...POSITIVE_CATEGORY_FAMILY,
  // Post-booking outcomes (admitted to the Appointment Booked view via the
  // booking-evidence guard)
  'no show',
  'not qualified',
  // Negative / neutral
  'not interested',
  'not_interested',
  'do not contact',
  'wrong person',
  'reach out later',
  'out of office',
  'out_of_office',
  'automated response',
  'automated reply',
  'automated_reply',
  'sender originated bounce',
  'uncategorizable by ai',
  // Other working categories observed in production
  'pending: need additional info before response',
  'information request',
  'meeting request',
  'forwarded',
  'sample request',
  'has phone number -> call them',
]);

/** Case/whitespace-insensitive membership test against the known set. */
export function isKnownSmartleadCategoryName(
  name: string | null | undefined
): boolean {
  if (!name) return false;
  return KNOWN_SMARTLEAD_CATEGORY_NAMES.has(name.toLowerCase().trim());
}

// ============================================================================
// CATEGORY IDs (2026-10-16 migration: names → ids)
//
// SmartLead renames categories IN PLACE (id stable, name mutates — e.g.
// 2026-10-16: 4507 'Sample Request' → 'DTC: Sample Request', 20602
// 'Forwarded' → 'DTC: Handed Over'). The dashboard therefore treats the
// category ID as the stable identity and names as display labels only.
// Canonical registry: DB table `smartlead_categories` (synced from
// GET /api/v1/leads/fetch-categories). The frozen literals below mirror the
// registry as of 2026-10-16 and exist so ingestion gates and SQL predicates
// never depend on a live lookup.
// ============================================================================

/** Stable SmartLead category IDs (registry snapshot 2026-10-16). */
export const CATEGORY_IDS = {
  INTERESTED: 1,
  MEETING_REQUEST: 2,
  NOT_INTERESTED: 3,
  DO_NOT_CONTACT: 4,
  INFORMATION_REQUEST: 5,
  OUT_OF_OFFICE: 6,
  WRONG_PERSON: 7,
  UNCATEGORIZABLE_BY_AI: 8,
  SENDER_ORIGINATED_BOUNCE: 9,
  APPOINTMENT_BOOKED: 240,
  REACH_OUT_LATER: 263,
  NOT_QUALIFIED: 394,
  AUTOMATED_RESPONSE: 419,
  /** Covers BOTH 'Automated Followups Begun' and 'Interested: Automated FU Begun'. */
  INTERESTED_AUTOMATED_FU: 428,
  HAS_PHONE_NUMBER_CALL: 1261,
  NO_SHOW: 3028,
  INTERESTED_MANUAL_FU: 3498,
  /** 'Sample Request', renamed 'DTC: Sample Request' 2026-10-16. */
  SAMPLE_REQUEST: 4507,
  /** 'Forwarded', renamed 'DTC: Handed Over' 2026-10-16. */
  FORWARDED: 20602,
  /** 'Appt Booked + Continue Pre-call Followup'. */
  APPOINTMENT_BOOKED_CONTINUE: 22719,
  PENDING_INFO: 111294,
  DTC_MISSING_POC: 170785,
  DTC_WHOLESALE_ORDER: 172501,
} as const;

/** Every category id in the registry snapshot. */
export const KNOWN_CATEGORY_IDS: ReadonlySet<number> = new Set(
  Object.values(CATEGORY_IDS)
);

/**
 * ID variant of INTERESTED_FAMILY_CATEGORIES. 'MQL' is deliberately absent:
 * its SmartLead id (135343) has never appeared in a webhook event, so MQL
 * stays name-matched via the legacy-name fallback.
 */
export const INTERESTED_FAMILY_CATEGORY_IDS: ReadonlySet<number> = new Set([
  CATEGORY_IDS.INTERESTED,
  CATEGORY_IDS.INTERESTED_AUTOMATED_FU,
  CATEGORY_IDS.INTERESTED_MANUAL_FU,
  CATEGORY_IDS.DTC_MISSING_POC,
  CATEGORY_IDS.DTC_WHOLESALE_ORDER,
  CATEGORY_IDS.SAMPLE_REQUEST,
  CATEGORY_IDS.FORWARDED,
]);

/** ID variant of POSITIVE_CATEGORY_FAMILY (family + Appointment Booked). */
export const POSITIVE_CATEGORY_FAMILY_IDS: ReadonlySet<number> = new Set([
  ...INTERESTED_FAMILY_CATEGORY_IDS,
  CATEGORY_IDS.APPOINTMENT_BOOKED,
]);

/**
 * Combined "Handed Over / Samples" dashboard section (2026-10-26). A virtual
 * composite — no single SmartLead category exists for it — used as the pinned
 * `current_category_name` semantic value for the /outcomes view. Expands
 * id-first to Sample Request (4507) + Forwarded/Handed Over (20602) +
 * 'DTC - Wholesale Order Created' (172501, added 2026-10-26: the section is
 * the RBAC-scoped visibility for the full post-appointment funnel, and
 * wholesale-order rows were previously only reachable on the admin-gated DTC
 * dashboard). The DTC dashboard's conversion KPI keeps counting these
 * categories independently — this expansion is display-only. Names are the
 * union of the member categories' observed spellings ('Handed Over' without
 * the 'DTC: ' prefix is live in production alongside 'DTC: Handed Over').
 */
export const HANDED_OVER_SAMPLES_CATEGORY_VALUE = 'Handed Over / Samples';

/** Map-key form (lowercased) used by the four category-expansion sites. */
export const HANDED_OVER_SAMPLES_CATEGORY_KEY = 'handed over / samples';

/** All observed spellings of the member categories. */
export const HANDED_OVER_SAMPLES_CATEGORY_NAMES: readonly string[] = [
  ...SAMPLE_REQUEST_CATEGORY_NAMES,
  ...FORWARDED_CATEGORY_NAMES,
  'Handed Over',
  DTC_WHOLESALE_ORDER_CATEGORY_NAME,
];

/** Member ids: [4507, 20602, 172501]. */
export const HANDED_OVER_SAMPLES_CATEGORY_IDS: readonly number[] = [
  CATEGORY_IDS.SAMPLE_REQUEST,
  CATEGORY_IDS.FORWARDED,
  CATEGORY_IDS.DTC_WHOLESALE_ORDER,
];

/**
 * Categories that set has_converted (webhook-processor step 14 + trigger
 * fn_auto_set_has_converted v2): Appointment Booked + Automated FU Begun.
 */
export const HAS_CONVERTED_CATEGORY_IDS: ReadonlySet<number> = new Set([
  CATEGORY_IDS.APPOINTMENT_BOOKED,
  CATEGORY_IDS.INTERESTED_AUTOMATED_FU,
]);

/**
 * Legacy category-name → id aliases (lowercased + trimmed). Covers every
 * spelling observed in production: current names, pre-rename names
 * ('sample request', 'forwarded'), and legacy SmartLead enum spellings
 * ('not_interested', 'automated_reply', 'out_of_office', hyphenated
 * 'automated follow-ups begun'). Used by the id backfill and as the
 * name-fallback in id-first predicates. Unresolvable on purpose: 'mql'
 * (no stable id ever observed in events).
 */
export const LEGACY_CATEGORY_NAME_TO_ID: Readonly<Record<string, number>> = {
  'interested': CATEGORY_IDS.INTERESTED,
  'meeting request': CATEGORY_IDS.MEETING_REQUEST,
  'not interested': CATEGORY_IDS.NOT_INTERESTED,
  'not_interested': CATEGORY_IDS.NOT_INTERESTED,
  'do not contact': CATEGORY_IDS.DO_NOT_CONTACT,
  'information request': CATEGORY_IDS.INFORMATION_REQUEST,
  'out of office': CATEGORY_IDS.OUT_OF_OFFICE,
  'out_of_office': CATEGORY_IDS.OUT_OF_OFFICE,
  'wrong person': CATEGORY_IDS.WRONG_PERSON,
  'uncategorizable by ai': CATEGORY_IDS.UNCATEGORIZABLE_BY_AI,
  'sender originated bounce': CATEGORY_IDS.SENDER_ORIGINATED_BOUNCE,
  'appointment booked': CATEGORY_IDS.APPOINTMENT_BOOKED,
  'reach out later': CATEGORY_IDS.REACH_OUT_LATER,
  'not qualified': CATEGORY_IDS.NOT_QUALIFIED,
  'automated response': CATEGORY_IDS.AUTOMATED_RESPONSE,
  'automated reply': CATEGORY_IDS.AUTOMATED_RESPONSE,
  'automated_reply': CATEGORY_IDS.AUTOMATED_RESPONSE,
  'automated followups begun': CATEGORY_IDS.INTERESTED_AUTOMATED_FU,
  'automated follow-ups begun': CATEGORY_IDS.INTERESTED_AUTOMATED_FU,
  'interested: automated fu begun': CATEGORY_IDS.INTERESTED_AUTOMATED_FU,
  'has phone number -> call them': CATEGORY_IDS.HAS_PHONE_NUMBER_CALL,
  'no show': CATEGORY_IDS.NO_SHOW,
  'interested: manual fu begun': CATEGORY_IDS.INTERESTED_MANUAL_FU,
  'sample request': CATEGORY_IDS.SAMPLE_REQUEST,
  'dtc: sample request': CATEGORY_IDS.SAMPLE_REQUEST,
  'forwarded': CATEGORY_IDS.FORWARDED,
  'dtc: handed over': CATEGORY_IDS.FORWARDED,
  'handed over': CATEGORY_IDS.FORWARDED,
  'handed over/sample requested': CATEGORY_IDS.FORWARDED,
  'appt booked + continue pre-call followup': CATEGORY_IDS.APPOINTMENT_BOOKED_CONTINUE,
  'pending: need additional info before response': CATEGORY_IDS.PENDING_INFO,
  'dtc - missing poc info': CATEGORY_IDS.DTC_MISSING_POC,
  'dtc - wholesale order created': CATEGORY_IDS.DTC_WHOLESALE_ORDER,
};

/**
 * Resolve a category to its stable id. Prefers the payload id when it is a
 * known registry id; otherwise resolves the (lowercased/trimmed) name through
 * the legacy alias map. Returns null for unknown ids AND unmappable names —
 * callers treat null as "unknown" (surface via the watch guard).
 */
export function resolveCategoryId(
  id: number | null | undefined,
  name: string | null | undefined
): number | null {
  if (id != null && KNOWN_CATEGORY_IDS.has(id)) return id;
  if (name != null && name.trim() !== '') {
    return LEGACY_CATEGORY_NAME_TO_ID[name.toLowerCase().trim()] ?? null;
  }
  return null;
}

/** Case/whitespace-insensitive id lookup for a name alone. */
export function categoryIdFromName(
  name: string | null | undefined
): number | null {
  return resolveCategoryId(null, name);
}
